import { useTranslation } from 'react-i18next';
import { cases } from '@/mocks/cases';
import { useInView } from '@/hooks/useInView';

export default function Cases() {
  const { t } = useTranslation();
  const { ref, inView } = useInView<HTMLDivElement>(0.1);

  return (
    <section id="cases" className="w-full bg-background-50 py-24 md:py-32">
      <div ref={ref} className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div>
            <div className={`text-xs font-mono-tech tracking-widest uppercase text-foreground-600 mb-6 ${inView ? 'animate-fade-up' : 'opacity-0'}`}>
              {t('cases.tag')}
            </div>
            <h2
              className={`font-heading text-foreground-950 text-4xl md:text-6xl leading-[1.05] font-semibold tracking-tight ${inView ? 'animate-fade-up' : 'opacity-0'}`}
              style={{ animationDelay: '0.1s' }}
            >
              {t('cases.title')}
            </h2>
            <p className={`mt-4 text-sm md:text-base text-foreground-600 max-w-md ${inView ? 'animate-fade-up' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
              {t('cases.desc')}
            </p>
          </div>
          <a
            href="#cases"
            className={`inline-flex items-center gap-2 text-sm font-mono-tech tracking-wider uppercase text-foreground-950 border-b border-foreground-950 pb-1 hover:gap-3 transition-all whitespace-nowrap cursor-pointer ${inView ? 'animate-fade-up' : 'opacity-0'}`}
            style={{ animationDelay: '0.3s' }}
          >
            {t('cases.viewAll')}
            <i className="ri-arrow-right-line text-base w-4 h-4 flex items-center justify-center" />
          </a>
        </div>

        <div
          data-product-shop
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5"
        >
          {cases.map((c, i) => (
            <article
              key={c.id}
              className={`group relative rounded-2xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-foreground-950 transition-all cursor-pointer ${inView ? 'animate-fade-up' : 'opacity-0'}`}
              style={{ animationDelay: `${0.3 + i * 0.1}s` }}
            >
              <div className="relative aspect-square overflow-hidden bg-background-200/60">
                <img
                  src={c.image}
                  alt={t(c.nameKey)}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                {c.featured && (
                  <div className="absolute top-3 left-3 px-3 py-1 bg-primary-500 text-foreground-950 text-[10px] font-mono-tech tracking-widest uppercase rounded-full">
                    {t('services.s.badge.hot')}
                  </div>
                )}
                <div className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-background-50/90 backdrop-blur-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <i className="ri-arrow-right-up-line text-lg text-foreground-950 w-5 h-5 flex items-center justify-center" />
                </div>
              </div>
              <div className="p-5">
                <div className="text-[10px] font-mono-tech tracking-widest uppercase text-foreground-500 mb-2">
                  {t(c.categoryKey)}
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-3">
                  {t(c.nameKey)}
                </h3>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary-500" />
                  <span className="text-xs text-foreground-700 font-mono-tech">
                    {t(c.tvlKey)}
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}